import {
  type ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import i18n from 'commons/libs/i18n'
import { Checkbox, Divider, FilterTag, Icon, Label } from 'components'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

export type MultiselectProps = {
  width?: number
  height?: number
  label?: string
  inputPlaceholder?: string
  optionsList: string[]
  selectedOptions: string[]
  onChange: (options: string[]) => void
  error?: boolean
  disabled?: boolean
  hideSearch?: boolean
  labelOptionsList?: Record<string, string> // TODO: optionList must be converted to a { label: string; value: string }[] type
}

const MultiselectComponent: ForwardRefRenderFunction<HTMLSelectElement, MultiselectProps> = (
  {
    width,
    height = 30,
    label,
    inputPlaceholder = i18n.t('common.placeholders.select'),
    optionsList,
    selectedOptions,
    onChange,
    error = false,
    disabled = false,
    hideSearch = false,
    labelOptionsList = {},
  },
  ref,
) => {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left')

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(fieldRef, () => setOpen(false))

  const allOptionsSelected = useMemo(
    () => selectedOptions.length === optionsList.length && optionsList.length > 0,
    [selectedOptions, optionsList],
  )

  const handleSelectAll = () => {
    if (allOptionsSelected) onChange([])
    else onChange(optionsList)
  }

  const handleRemove = (option: string) => {
    onChange(selectedOptions.filter((selected) => selected !== option))
  }

  const handleSelectSingle = (option: string) => {
    if (selectedOptions.includes(option)) {
      onChange(selectedOptions.filter((item) => item !== option))
    } else {
      onChange([...selectedOptions, option])
    }
    inputRef.current?.focus()
  }

  const isOptionSelected = (option: string) => {
    return selectedOptions.includes(option)
  }

  const toggleDropdownVisibility = () => {
    setOpen((prev) => !prev)
  }

  const options = useMemo(() => {
    if (searchValue === '') return optionsList
    return optionsList.filter((opt) =>
      opt.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
    )
  }, [searchValue, optionsList])

  useEffect(() => {
    if (open) inputRef.current?.focus()
    else {
      setSearchValue('')
      if (containerRef.current?.scrollTo) containerRef.current?.scrollTo(0, 0)
    }
  }, [open])

  useEffect(() => {
    if (open && fieldRef.current) {
      const rect = fieldRef.current.getBoundingClientRect()
      const screenWidth = window.innerWidth

      if (rect.right + 300 > screenWidth) {
        setDropdownPosition('right')
      } else {
        setDropdownPosition('left')
      }
    }
  }, [open])

  return (
    <S.MultiselectContainer ref={wrapperRef} width={width}>
      {label && (
        <S.LabelWrapper disabled={disabled}>
          <Label id="multiselect-label">{label}</Label>
        </S.LabelWrapper>
      )}
      <S.FieldWrapper
        data-testid="field-wrapper"
        disabled={disabled}
        error={error}
        height={height}
        onClick={toggleDropdownVisibility}
        ref={fieldRef || ref}
      >
        <S.FieldContainer ref={containerRef} width={wrapperRef.current?.clientWidth}>
          {allOptionsSelected ? (
            <FilterTag
              content={i18n.t('common.placeholders.all')}
              disabled={disabled}
              key={`filter-tag-all-selected`}
            />
          ) : (
            selectedOptions.map((option) => (
              <FilterTag
                content={labelOptionsList[option] || option}
                disabled={disabled}
                key={`filter-tag-${option}`}
                onRemove={(e) => {
                  e.stopPropagation()
                  handleRemove(option)
                }}
              />
            ))
          )}
          <S.SearchInput
            data-testid="multiselect-input"
            disabled={disabled || hideSearch}
            height={height}
            onChange={(e) => setSearchValue(e.target.value)}
            onClick={(e) => {
              e.stopPropagation()
              setOpen(true)
            }}
            placeholder={!selectedOptions.length ? inputPlaceholder : ''}
            ref={inputRef}
            value={searchValue}
          />
        </S.FieldContainer>

        <S.DropdownButton
          data-testid="dropdown-button"
          disabled={disabled}
          height={height}
          open={open}
          type="button"
        >
          <Icon icon="chevron-up" size="20" />
        </S.DropdownButton>
        {open && (
          <S.DropdownContainer
            height={height}
            onClick={(e) => e.stopPropagation()}
            position={dropdownPosition}
          >
            <S.OptionsWrapper data-testid="multiselect-options">
              {!searchValue && (
                <S.OptionRow key={`multiselect-option-selectall`}>
                  <S.SelectAllButton
                    checked={allOptionsSelected}
                    onClick={() => handleSelectAll()}
                    type="button"
                  >
                    <Icon
                      color={theme.colors.ion.primary.main}
                      icon={allOptionsSelected ? 'copy-check' : 'copy'}
                      size={theme.icon.size.small}
                    />
                    <S.OptionSelectAllText data-testid="option-select-all-text">
                      {i18n.t('common.placeholders.selectAll')}
                    </S.OptionSelectAllText>
                  </S.SelectAllButton>
                </S.OptionRow>
              )}

              <Divider marginBottom={theme.spacing.xs} />

              {options.map((option) => (
                <S.OptionRow
                  data-testid={`option-row-${option}`}
                  key={`multiselect-option-${option}`}
                >
                  <Checkbox
                    checked={isOptionSelected(option)}
                    onChange={() => handleSelectSingle(option)}
                    size="small"
                  />
                  <S.OptionText>{labelOptionsList[option] || option}</S.OptionText>
                </S.OptionRow>
              ))}
              {!options.length && (
                <S.OptionRow key={`multiselect-option-not-found`}>
                  <S.OptionText data-testid="empty-search-message">
                    {i18n.t('common.messages.emptySearchMessage')}
                  </S.OptionText>
                </S.OptionRow>
              )}
            </S.OptionsWrapper>
          </S.DropdownContainer>
        )}
      </S.FieldWrapper>
    </S.MultiselectContainer>
  )
}

export const Multiselect = forwardRef(MultiselectComponent)
